import React, { useState, useEffect, useCallback, memo, useMemo } from "react";
import { Box, IconButton, InputAdornment, lighten, Paper, TextField, ThemeProvider, Typography } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../store";
import Theme from "../utils/theme";
import GenerationRecipe from "../components/GenerationRecipe";
import Loading from "../components/Loading";
import { tips } from "../utils/tips";
import { AutoAwesome } from "@mui/icons-material";
import PageIndicator from "../components/PageIndicator";
import { scrollbarCss } from "../utils/themeScrollbar";
import LoadingWithTip from "../components/LoadingWithTip";
import { generateRecipeByName } from "../utils/helpers/recipeHelper";

// Component Constants
const STEPS = [0, 1];
const MemoizedPageIndicator = memo(PageIndicator);
const randomTip = tips[Math.floor(Math.random() * tips.length)];

// ***** Main Compnent ***** //
const GenerateName: React.FC = () => {
    // *** Global States *** //
    const recipesState = useSelector((state: RootState) => state.recipes);
    const recentRecipes = recipesState.recentName;
    const authState = useSelector((state: RootState) => state.auth);
    const isGuest = !authState.account;
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    // *** Component States *** //
    const [loading, setLoading] = useState(true);
    const [recipeLoading, setRecipeLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [step, setStep] = useState<number>(recentRecipes?.length ? 1 : 0);
    const [prompt, setPrompt] = useState<string>("");

    // *** Component Helpers *** //
    const dispatch = useDispatch();
    const capitalize = useCallback((word: string) => word.charAt(0).toUpperCase() + word.slice(1), []);

    const handleRecipeGenerate = useCallback(() => {
        if (recipeLoading) return;

        setRecipeLoading(true);

        generateRecipeByName(prompt, isGuest ? null : authState.account?.username, dispatch)
            .catch((error) => {
                setError(error);
            })
            .finally(() => {
                setRecipeLoading(false);
                setStep(1);
            });
    }, [authState.account?.username, dispatch, isGuest, prompt, recipeLoading]);

    const renderGenerateTextField = useMemo(() => {
        return (
            <Box sx={{ px: 2, pt: 3, pb: 1, maxWidth: 1200 }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: 4,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                        backgroundColor: "rgba(255, 255, 255, 0.12)",
                        backdropFilter: "blur(14px)",
                        WebkitBackdropFilter: "blur(14px)",
                        border: "1px solid rgba(255, 255, 255, 0.35)",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                            component="img"
                            src={"/images/GenerateFeatureIcon.png"}
                            sx={{
                                width: 34,
                                height: 34,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: (theme) =>
                                    `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                                color: "white",
                                fontSize: 20,
                            }}
                        />
                        <Box>
                            <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                                Type a recipe name or describe your craving – CooCoo will do the rest.
                            </Typography>
                        </Box>
                    </Box>

                    <TextField
                        fullWidth
                        placeholder='e.g. "Creamy lemon pasta with spinach" or "something cozy with pumpkin and cinnamon"...'
                        onChange={(e) => setPrompt(e.target.value)}
                        multiline
                        minRows={2}
                        maxRows={4}
                        variant="outlined"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end" sx={{ alignSelf: "flex-start", mt: 0.5 }}>
                                    <IconButton
                                        onClick={handleRecipeGenerate}
                                        edge="end"
                                        sx={{
                                            cursor: "pointer",
                                            mr: 0.5,
                                            width: 36,
                                            height: 36,
                                            borderRadius: "50%",
                                            background: (theme) =>
                                                `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                                            color: "white",
                                            boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
                                            "&:hover": {
                                                background: (theme) =>
                                                    `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                            },
                                        }}
                                    >
                                        {recipeLoading ? <Loading color="white" size="20px" /> : <AutoAwesome sx={{ fontSize: 20 }} />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            backgroundColor: lighten(theme.palette.text.secondary, 0.9),
                            borderRadius: 5,
                            mt: 1,
                            textarea: {
                                ...scrollbarCss,
                            },
                        }}
                    />

                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5, gap: 1 }}>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            Tip: The more detail you give, the better the match.
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                fontFamily: "Playfair Display",
                                fontWeight: 500,
                                display: { xs: "none", sm: "inline" },
                            }}
                        >
                            Press Enter when you’re ready ✨
                        </Typography>
                    </Box>
                </Paper>
            </Box>
        );
    }, [handleRecipeGenerate, recipeLoading, theme.palette.text.secondary]);

    const renderRecentRecipes = useMemo(() => {
        if (!recentRecipes || recentRecipes.length === 0) {
            return (
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                    <Typography variant="subtitle2" sx={{ fontSize: "16px", mt: 5, textAlign: "center" }}>
                        No Recipes Generated!
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontSize: "16px", mt: 5, textAlign: "center" }}>
                        Please go back to the previous step.
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ flex: 1, position: "relative", justifyItems: "center", alignContent: "center", width: "100%" }}>
                <Box
                    sx={{
                        maxWidth: 1200,
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        px: 2,
                        "&::-webkit-scrollbar": { display: "none" },
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                    }}
                >
                    {loading ? (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                            <Loading />
                            <Typography variant="h6" sx={{ fontFamily: "Playfair Display", fontWeight: 600, mt: 3 }}>
                                {randomTip}
                            </Typography>
                        </Box>
                    ) : error ? (
                        <Typography sx={{ mt: "15%" }}>{error}</Typography>
                    ) : recentRecipes?.length === 0 ? (
                        <Typography variant="h6" sx={{ mt: "15%", textAlign: "center" }}>
                            No Recipes generated yet!
                        </Typography>
                    ) : (
                        <GenerationRecipe recentRecipes={recentRecipes} type="name" />
                    )}
                </Box>
            </Box>
        );
    }, [error, loading, recentRecipes]);

    // *** Use Effects *** //
    useEffect(() => {
        setLoading(false);
    }, [capitalize]);

    if (loading)
        return (
            <Box
                sx={{
                    height: "calc(100dvh - 200px)",
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <LoadingWithTip />
            </Box>
        );

    return (
        <ThemeProvider theme={theme}>
            <MemoizedPageIndicator steps={STEPS} setStep={setStep} step={step} />

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    width: "100%",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                {step === 0 && renderGenerateTextField}
                {step === 1 && renderRecentRecipes}
            </Box>
        </ThemeProvider>
    );
};

export default GenerateName;
